/**
 * GENERATED_ASSET — three-window paper lantern ported from the batch001 static kit (`static-kit/source/lantern.ts`).
 * Art is unchanged; only layout was expanded for readability.
 *
 * Hanging art paints a real hook (`data-part="hanging-hook"`) and exports a `hang` anchor. Standing art paints a
 * contact shadow + wooden foot (`standing-shadow`, `standing-base`) and exports NO hanging anchor.
 * The master two-over-one cutouts never enter the outline or wear seed streams.
 */
import type { DraftArtifact, LanternRecipe, Point2, ProtectedVisualRegion } from './contracts';
import { Construction, anchor, el, n } from './construction';

export function buildThreeWindowLantern(c: Construction): DraftArtifact {
  const r = c.recipe as LanternRecipe;
  const p = r.params;
  const s = c.tokens.style;
  const shift = (id: string): number => (c.random(`outline.${id}`) - 0.5) * 1.5;
  const points: Point2[] = [
    { x: -30 + shift('a'), y: -84 }, { x: 19, y: -90 }, { x: 36, y: -63 + shift('b') },
    { x: 26 + shift('c'), y: -10 }, { x: -21, y: -5 }, { x: -37, y: -43 },
  ];
  const d = points.map((v, i) => `${i ? 'L' : 'M'}${n(v.x)} ${n(v.y)}`).join(' ') + 'Z';
  const paper = c.define('paper', (id) => el('linearGradient', { id, x1: -26, y1: -85, x2: 19, y2: -7, gradientUnits: 'userSpaceOnUse' },
    el('stop', { offset: 0, 'stop-color': c.color(p.condition === 'interior_warm' ? 'warmCore' : 'paperLight') })
    + el('stop', { offset: 1, 'stop-color': c.color(p.condition === 'dry' ? 'paperLight' : 'paperBase') })));
  const clip = c.define('face', (id) => el('clipPath', { id }, el('path', { d })));

  let body = '';
  if (p.support === 'standing') {
    body += c.ellipse(0, 2, 30, 3, c.color('outline'), { opacity: s.contactShadowOpacity, 'data-part': 'standing-shadow' }, false);
  }
  if (p.support === 'hanging') {
    body += c.path('M0 -86V-118Q0 -124 8 -121Q19 -118 14 -106',
      [{ x: 0, y: -86 }, { x: 0, y: -118 }, { x: 0, y: -124 }, { x: 8, y: -121 }, { x: 19, y: -118 }, { x: 14, y: -106 }],
      'none', c.color('woodShadow'), 3.5, { 'data-part': 'hanging-hook' });
  }
  body += c.path(d, points, paper, c.color('outline'), s.outerStroke, { 'data-part': 'paper-body' });
  body += c.path('M19 -90L36 -63L26 -10L14 -14L23 -63Z',
    [{ x: 19, y: -90 }, { x: 36, y: -63 }, { x: 26, y: -10 }, { x: 14, y: -14 }, { x: 23, y: -63 }],
    c.color('paperShadow'), undefined, 0, { opacity: 0.38 }, false);

  let fibres = '';
  const count = c.tokens.detailTiers[r.detail].grainMarks;
  for (let i = 0; i < count; i++) {
    const x = -31 + c.random(`paper-grain.${i}.x`) * 53;
    const y = -82 + c.random(`paper-grain.${i}.y`) * 64;
    // Marks stay in the face and are painted before the opaque inset lips.
    fibres += c.path(`M${n(x)} ${n(y)}q5 -1 10 1`, [{ x, y }, { x: x + 5, y: y - 1 }, { x: x + 10, y: y + 1 }],
      'none', c.color('paperShadow'), s.grainStroke, { opacity: s.grainOpacity * p.paperWear }, false);
  }
  body += el('g', { 'clip-path': clip, 'data-part': 'paper-fibres' }, fibres);
  if (p.condition === 'salt_worn' && r.detail !== 'readable') {
    body += el('g', { 'clip-path': clip, opacity: p.paperWear * 0.5, 'data-part': 'salt-wear' },
      c.path('M-30 -76l8 -1M-32 -66l5 1M21 -17l-7 1', [{ x: -32, y: -77 }, { x: 22, y: -16 }],
        'none', c.color('salt'), 1.3, {}, false));
  }

  const windows = c.tokens.masterMotif.centers;
  for (let i = 0; i < windows.length; i++) {
    const w = windows[i];
    const rad = c.tokens.masterMotif.radius;
    body += el('g', { 'data-part': 'clue-window', 'data-window-index': i },
      c.ellipse(w.x, w.y, rad + 1.7, rad + 1.7, c.color('paperShadow'), {}, false)
      + c.ellipse(w.x, w.y, rad, rad, c.color('outline'), {}, false)
      + c.ellipse(w.x + 0.4, w.y + 0.6, rad - 1.7, rad - 1.7, c.color('warmCore'), { opacity: 0.38 + p.warmth * 0.58 }, false));
  }
  body += c.path('M-29 -79L17 -85M-33 -42L-19 -10L24 -15',
    [{ x: -29, y: -79 }, { x: 17, y: -85 }, { x: -33, y: -42 }, { x: -19, y: -10 }, { x: 24, y: -15 }],
    'none', c.color('paperLight'), s.seamStroke, { 'data-part': 'paper-seams' }, false);

  if (p.repairNotch !== 'none') {
    const x = p.repairNotch === 'lower-left' ? -19 : 17;
    body += c.path(`M${x - 5} -18l3 -6 7 3 -1 10Z`, [{ x: x - 5, y: -24 }, { x: x + 5, y: -11 }],
      c.color('paperLight'), c.color('outlineSoft'), 0.8, { 'data-part': 'paper-repair' }, false);
    // A broad repair remains visible at readable tier; only extra stitches are reduced.
    body += c.path(`M${x - 2} -21v4m4 -3v4`, [{ x: x - 2, y: -21 }, { x: x + 2, y: -16 }],
      'none', c.color('woodShadow'), r.detail === 'readable' ? 0.9 : 0.7, {}, false);
  }
  if (p.support === 'standing') {
    body += c.path('M-21 -7L26 -12L23 0H-19Z', [{ x: -21, y: -7 }, { x: 26, y: -12 }, { x: 23, y: 0 }, { x: -19, y: 0 }],
      c.color('woodBase'), c.color('outline'), 1.3, { 'data-part': 'standing-base' });
  }
  body += c.path('M23 -84L29 -77L25 -72', [{ x: 23, y: -84 }, { x: 29, y: -77 }, { x: 25, y: -72 }],
    'none', c.color('woodShadow'), 1.6, { 'data-part': 'ribbon-eye' }, false);

  const regions: ProtectedVisualRegion[] = windows.map((w, i) => ({
    name: `clue_window_${i}`, role: 'motif', bounds: { x: w.x - 10, y: w.y - 10, width: 20, height: 20 }, gameplayAuthority: false,
  }));
  regions.push({ name: 'clue_windows', role: 'motif', bounds: { x: -22, y: -66, width: 44, height: 44 }, gameplayAuthority: false });
  const origin = p.support === 'hanging'
    ? { kind: 'attachment' as const, point: { x: 0, y: -118 } }
    : { kind: 'bottom-center' as const, point: { x: 0, y: 0 } };
  return c.finish(body, [
    ...(p.support === 'hanging' ? [anchor('hang', 0, -118)] : []),
    anchor('support', origin.point.x, origin.point.y, 'support'),
    anchor('ribbon_tie', 29, -77),
    anchor('light_core', 0, -48, 'effect'),
    anchor('mote_origin', 3, -53, 'effect'),
  ], regions, origin, { windows: windows.map((w) => ({ ...w, radius: 7.5 })), support: p.support, paperRepair: p.repairNotch, outline: points });
}
